import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, SimpleGrid, Text, Flex, HStack, VStack, Icon, Input, Select,
    Button, ButtonGroup, Spinner, Tooltip, Badge, Alert, AlertIcon, AlertDescription,
} from '@chakra-ui/react';
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    ArcElement, Title, Tooltip as ChartTooltip, Legend,
    BarController, LineController,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
    FaDownload, FaCalendarAlt, FaBoxes, FaClock, FaArrowDown, FaLock,
    FaHandHoldingUsd, FaCheckCircle, FaExclamationTriangle, FaSearch,
    FaInfoCircle, FaCommentDots, FaFlag, FaChartLine,
} from 'react-icons/fa';
import { getDevolucoesMetricas, exportDevolucoesMetricasCsv } from '../../services/api';
import { formatBRL } from '../../utils/formatters';
import ChartCard from '../ExpandableChartCard';
import dayjs from 'dayjs';

ChartJS.register(
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    ArcElement, Title, ChartTooltip, Legend, BarController, LineController
);

const OWNER_COLORS = {
    buyer:   '#E53E3E',
    ml:      '#D69E2E',
    seller:  '#38A169',
    unknown: '#718096',
};

const URGENCY_COLORS = {
    atrasada:  '#C53030',
    urgente:   '#E53E3E',
    proxima:   '#DD6B20',
    normal:    '#38A169',
    sem_prazo: '#A0AEC0',
};

const STATUS_COLORS = {
    opened:          '#3182CE',
    label_generated: '#00B5D8',
    shipped:         '#0BC5EA',
    delivered:       '#805AD5',
    closed:          '#38A169',
    not_delivered:   '#E53E3E',
    cancelled:       '#A0AEC0',
    failed:          '#E53E3E',
    expired:         '#DD6B20',
};

const CONTA_COLORS = { 0: '#319795', 1: '#F68633', 2: '#805AD5', 3: '#1baf7a' };

const KpiCard = ({ icon, iconColor, title, value, footer, bg, borderColor, valueColor }) => (
    <Box
        bg={bg || 'white'}
        p={{ base: 3, md: 4 }}
        borderRadius="lg"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor || 'gray.200'}
        transition="box-shadow 0.15s, transform 0.15s"
        _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }}
    >
        <HStack spacing={2} mb={1}>
            {icon && <Icon as={icon} color={iconColor || 'gray.500'} boxSize={4} />}
            <Text fontSize="11px" fontWeight="bold" color="gray.600" letterSpacing="wider" textTransform="uppercase" noOfLines={1}>
                {title}
            </Text>
        </HStack>
        <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color={valueColor || 'gray.800'} lineHeight="1.1">
            {value}
        </Text>
        {footer && (
            <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={1}>
                {footer}
            </Text>
        )}
    </Box>
);

const PRESETS = [
    { label: '30 dias', days: 30 },
    { label: '90 dias', days: 90 },
    { label: '6 meses', days: 180 },
    { label: '1 ano',  days: 365 },
    { label: 'Tudo',   days: null },
];

const DevolucoesMetricas = () => {
    const hoje = dayjs().format('YYYY-MM-DD');
    const inicioBase = dayjs().subtract(12, 'month').format('YYYY-MM-DD');

    const [dataInicial, setDataInicial] = useState(inicioBase);
    const [dataFinal, setDataFinal] = useState(hoje);
    const [conta, setConta] = useState('');
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exportando, setExportando] = useState(false);
    const [modoVendas, setModoVendas] = useState('qtd');

    const carregar = async () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (dataInicial) params.append('data_inicial', dataInicial);
        if (dataFinal) params.append('data_final', dataFinal);
        if (conta) params.append('conta', conta);
        const resp = await getDevolucoesMetricas(params.toString());
        setDados(resp);
        setLoading(false);
    };

    useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

    const aplicarPreset = (days) => {
        if (days === null) {
            const minData = dados?.global?.data_min;
            setDataInicial(minData ? dayjs(minData).format('YYYY-MM-DD') : '2023-01-01');
        } else {
            setDataInicial(dayjs().subtract(days, 'day').format('YYYY-MM-DD'));
        }
        setDataFinal(hoje);
    };

    const exportar = async ({ moneyOwner = null, respeitarPeriodo = false } = {}) => {
        setExportando(true);
        try {
            const params = new URLSearchParams();
            if (respeitarPeriodo) {
                if (dataInicial) params.append('data_inicial', dataInicial);
                if (dataFinal) params.append('data_final', dataFinal);
            } else {
                params.append('all', 'true');
            }
            if (conta) params.append('conta', conta);
            if (moneyOwner) params.append('money_owner', moneyOwner);

            const blob = await exportDevolucoesMetricasCsv(params.toString());
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const ownerSuf = { buyer: '_devolvido', ml: '_retido', seller: '_recuperado', unknown: '_indefinido' }[moneyOwner] || '';
            const periodoSuf = respeitarPeriodo ? `_${dataInicial}_a_${dataFinal}` : '_tudo';
            a.download = `devolucoes${ownerSuf}${periodoSuf}_${dayjs().format('HHmmss')}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } finally {
            setExportando(false);
        }
    };

    const charts = useMemo(() => {
        if (!dados) return null;

        const volumeData = {
            labels: dados.volume_mensal.map(m => m.label),
            datasets: [{
                label: 'Devoluções',
                data: dados.volume_mensal.map(m => m.qtd),
                borderColor: '#F68633',
                backgroundColor: 'rgba(246, 134, 51, 0.2)',
                tension: 0.3,
                fill: true,
                pointRadius: 4,
                pointHoverRadius: 6,
            }],
        };

        const donutData = {
            labels: dados.donut_owner.map(o => o.label),
            datasets: [{
                data: dados.donut_owner.map(o => o.qtd),
                backgroundColor: dados.donut_owner.map(o => OWNER_COLORS[o.owner] || '#CBD5E0'),
                borderColor: '#fff',
                borderWidth: 2,
            }],
        };

        const statusData = {
            labels: dados.status_funil.map(s => s.label),
            datasets: [{
                label: 'Quantidade',
                data: dados.status_funil.map(s => s.qtd),
                backgroundColor: dados.status_funil.map(s => STATUS_COLORS[s.status] || '#CBD5E0'),
                borderRadius: 6,
            }],
        };

        const urgenciaData = {
            labels: dados.urgencia_dist.map(u => u.label),
            datasets: [{
                label: 'Quantidade',
                data: dados.urgencia_dist.map(u => u.qtd),
                backgroundColor: dados.urgencia_dist.map(u => URGENCY_COLORS[u.level] || '#CBD5E0'),
                borderRadius: 6,
            }],
        };

        const fornecedoresData = {
            labels: dados.top_fornecedores.map(f => f.nome),
            datasets: [{
                label: 'Devoluções',
                data: dados.top_fornecedores.map(f => f.qtd),
                backgroundColor: '#F68633',
                borderRadius: 4,
            }],
        };

        const produtosData = {
            labels: dados.top_produtos.map(p => truncate(p.nome, 40)),
            datasets: [{
                label: 'Devoluções',
                data: dados.top_produtos.map(p => p.qtd),
                backgroundColor: '#805AD5',
                borderRadius: 4,
            }],
        };

        const resolucoesData = {
            labels: dados.top_motivos.map(m => truncate(m.motivo, 50)),
            datasets: [{
                label: 'Ocorrências',
                data: dados.top_motivos.map(m => m.qtd),
                backgroundColor: '#3182CE',
                borderRadius: 4,
            }],
        };

        const motivosReaisData = {
            labels: (dados.top_motivos_reais || []).map(m => truncate(m.motivo, 60)),
            datasets: [{
                label: 'Ocorrências',
                data: (dados.top_motivos_reais || []).map(m => m.qtd),
                backgroundColor: '#E53E3E',
                borderRadius: 4,
            }],
        };

        const contas = dados.por_conta.sort((a, b) => a.conta - b.conta);
        const porContaData = {
            labels: contas.map(c => (c.conta ? `Conta ${c.conta}` : 'Devoluções Manuais')),
            datasets: [
                {
                    label: 'Qtd Devoluções',
                    data: contas.map(c => c.qtd),
                    backgroundColor: contas.map(c => CONTA_COLORS[c.conta] || '#A0AEC0'),
                    borderRadius: 6,
                    yAxisID: 'y',
                },
                {
                    label: 'R$ Devolvido',
                    data: contas.map(c => c.perdido),
                    backgroundColor: contas.map(c => (CONTA_COLORS[c.conta] || '#A0AEC0') + 'AA'),
                    borderRadius: 6,
                    yAxisID: 'y1',
                },
            ],
        };

        return { volumeData, donutData, statusData, urgenciaData, fornecedoresData, produtosData, resolucoesData, motivosReaisData, porContaData };
    }, [dados]);

    const vendasVsDevChart = useMemo(() => {
        if (!dados?.vendas_vs_devolucoes?.length) return null;
        const vd = dados.vendas_vs_devolucoes;
        const isQtd = modoVendas === 'qtd';
        const mat = vd.map(m => m.maturando);
        return {
            labels: vd.map(m => m.label),
            datasets: [
                {
                    type: 'bar',
                    label: isQtd ? 'Volume de vendas' : 'Vendas (R$)',
                    yAxisID: 'y',
                    order: 2,
                    data: vd.map(m => (isQtd ? m.vendas_qtd : m.vendas_valor)),
                    backgroundColor: '#CBD5E0',
                    borderRadius: 4,
                },
                {
                    type: 'bar',
                    label: isQtd ? 'Geraram devolução' : 'Devolvido (R$)',
                    yAxisID: 'y',
                    order: 1,
                    data: vd.map(m => (isQtd ? m.devolvidas_qtd : m.devolvidas_valor)),
                    backgroundColor: mat.map(x => (x ? '#FEB2B2' : '#E53E3E')),
                    borderRadius: 4,
                },
                {
                    type: 'line',
                    label: 'Taxa de devolução %',
                    yAxisID: 'y1',
                    order: 0,
                    data: vd.map(m => (isQtd ? m.taxa_qtd : m.taxa_valor)),
                    borderColor: '#805AD5',
                    backgroundColor: '#805AD5',
                    tension: 0.3,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    segment: { borderDash: ctx => (mat[ctx.p1DataIndex] ? [6, 4] : undefined) },
                },
            ],
        };
    }, [dados, modoVendas]);

    const optionsVendasVsDev = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'bottom' },
            tooltip: {
                callbacks: {
                    label: ctx => {
                        const v = ctx.parsed.y;
                        if (ctx.dataset.yAxisID === 'y1') return `${ctx.dataset.label}: ${v}%`;
                        return `${ctx.dataset.label}: ${modoVendas === 'valor' ? formatBRL(v) : v}`;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true, position: 'left',
                title: { display: true, text: modoVendas === 'valor' ? 'R$' : 'Qtd' },
                ticks: {
                    precision: 0,
                    callback: modoVendas === 'valor' ? (v => formatBRL(v).replace('R$', '').trim()) : undefined,
                },
            },
            y1: {
                beginAtZero: true, position: 'right', grid: { drawOnChartArea: false },
                title: { display: true, text: 'Taxa %' },
                ticks: { callback: v => `${v}%` },
            },
        },
    };

    const optionsVertical = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };
    const optionsHorizontal = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
    };
    const optionsLine = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };
    const optionsDonut = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12 } } },
        cutout: '60%',
    };
    const optionsDuplo = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: {
            y: { type: 'linear', position: 'left', beginAtZero: true, title: { display: true, text: 'Qtd' } },
            y1: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, title: { display: true, text: 'R$' },
                  ticks: { callback: (v) => formatBRL(v).replace('R$', '').trim() } },
        },
    };

    return (
        <Box w="100%" mt={2}>
            <Box bg="gray.50" borderWidth="1px" borderColor="#FF9F59" borderRadius="lg" p={{ base: 3, md: 4 }} boxShadow="md" mb={4}>
                <Text
                    fontSize="8px" textAlign="center" color="#F68633" fontWeight="bold"
                    position="relative" top="-22px" bg="white" w="80px" px={1} borderRadius="lg"
                    border="1px solid #FF9F59"
                >
                    MÉTRICAS
                </Text>
                <Flex wrap="wrap" gap={3} align="flex-end" mt={-4}>
                    <Box>
                        <Text fontSize="10px" fontWeight="bold" color="#F68633">PERÍODO</Text>
                        <HStack spacing={1}>
                            <Input size="sm" type="date" bg="white" value={dataInicial} max={dataFinal}
                                onChange={e => setDataInicial(e.target.value)} w="145px" />
                            <Text fontSize="sm" color="gray.500">até</Text>
                            <Input size="sm" type="date" bg="white" value={dataFinal} min={dataInicial}
                                onChange={e => setDataFinal(e.target.value)} w="145px" />
                        </HStack>
                    </Box>
                    <Box>
                        <Text fontSize="10px" fontWeight="bold" color="#F68633">CONTA</Text>
                        <Select size="sm" bg="white" value={conta} onChange={e => setConta(e.target.value)} w="130px">
                            <option value="">Todas</option>
                            <option value="1">Conta 1</option>
                            <option value="2">Conta 2</option>
                            <option value="3">Conta 3</option>
                        </Select>
                    </Box>
                    <HStack spacing={1} alignSelf="flex-end">
                        {PRESETS.map(p => (
                            <Button key={p.label} size="xs" variant="outline" colorScheme="orange" onClick={() => aplicarPreset(p.days)}>
                                {p.label}
                            </Button>
                        ))}
                    </HStack>
                    <Button size="sm" colorScheme="orange" leftIcon={<FaSearch />} onClick={carregar} isLoading={loading}>
                        Aplicar
                    </Button>
                </Flex>
            </Box>

            <Box
                bg="gray.50"
                borderWidth="1px"
                borderColor="#FF9F59"
                borderRadius="lg"
                p={{ base: 3, md: 4 }}
                boxShadow="md"
                mb={4}
            >
                <Text
                    fontSize="8px" textAlign="center" color="#F68633" fontWeight="bold"
                    position="relative" top="-22px" bg="white" w="90px" px={1} borderRadius="lg"
                    border="1px solid #FF9F59"
                >
                    EXPORTAÇÃO
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3} mt={-4}>
                    <Button
                        variant="outline"
                        colorScheme="orange"
                        leftIcon={<FaDownload />}
                        onClick={() => exportar({ moneyOwner: null })}
                        isLoading={exportando}
                        isDisabled={loading}
                        size="md"
                        h="auto"
                        py={3}
                        whiteSpace="normal"
                        textAlign="left"
                    >
                        <VStack align="flex-start" spacing={0} w="100%">
                            <Text fontWeight="bold" fontSize="sm">Exportar tudo</Text>
                            <Text fontSize="10px" color="gray.600" fontWeight="normal">
                                Todas as {dados?.global?.total_devolucoes || ''} devoluções
                            </Text>
                        </VStack>
                    </Button>
                    <Button
                        variant="outline"
                        colorScheme="red"
                        leftIcon={<FaArrowDown />}
                        onClick={() => exportar({ moneyOwner: 'buyer' })}
                        isLoading={exportando}
                        isDisabled={loading}
                        size="md"
                        h="auto"
                        py={3}
                        whiteSpace="normal"
                        textAlign="left"
                    >
                        <VStack align="flex-start" spacing={0} w="100%">
                            <Text fontWeight="bold" fontSize="sm">Devolvido ao cliente</Text>
                            <Text fontSize="10px" color="gray.600" fontWeight="normal">
                                money_owner = buyer
                            </Text>
                        </VStack>
                    </Button>
                    <Button
                        variant="outline"
                        colorScheme="yellow"
                        leftIcon={<FaLock />}
                        onClick={() => exportar({ moneyOwner: 'ml' })}
                        isLoading={exportando}
                        isDisabled={loading}
                        size="md"
                        h="auto"
                        py={3}
                        whiteSpace="normal"
                        textAlign="left"
                    >
                        <VStack align="flex-start" spacing={0} w="100%">
                            <Text fontWeight="bold" fontSize="sm">Retido pelo ML</Text>
                            <Text fontSize="10px" color="gray.600" fontWeight="normal">
                                money_owner = ml
                            </Text>
                        </VStack>
                    </Button>
                    <Button
                        variant="outline"
                        colorScheme="green"
                        leftIcon={<FaHandHoldingUsd />}
                        onClick={() => exportar({ moneyOwner: 'seller' })}
                        isLoading={exportando}
                        isDisabled={loading}
                        size="md"
                        h="auto"
                        py={3}
                        whiteSpace="normal"
                        textAlign="left"
                    >
                        <VStack align="flex-start" spacing={0} w="100%">
                            <Text fontWeight="bold" fontSize="sm">Valor recuperado</Text>
                            <Text fontSize="10px" color="gray.600" fontWeight="normal">
                                money_owner = seller
                            </Text>
                        </VStack>
                    </Button>
                </SimpleGrid>
            </Box>

            {loading && !dados ? (
                <Flex justify="center" align="center" h="400px">
                    <Spinner size="xl" color="#F68633" thickness="4px" />
                </Flex>
            ) : !dados ? (
                <Box textAlign="center" p={10} bg="red.50" borderRadius="lg" borderWidth="1px" borderColor="red.200">
                    <Text color="red.700" fontWeight="bold">Erro ao carregar métricas</Text>
                </Box>
            ) : (
                <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3}>
                        <KpiCard
                            icon={FaBoxes} iconColor="orange.500"
                            title="Total"
                            value={dados.kpis.total_devolucoes}
                            footer="devoluções no período"
                            valueColor="orange.700"
                        />
                        <KpiCard
                            icon={FaArrowDown} iconColor="red.500" bg="red.50" borderColor="red.200"
                            title="Devolvido"
                            value={formatBRL(dados.kpis.perdido)}
                            footer="ao cliente"
                            valueColor="red.700"
                        />
                        <KpiCard
                            icon={FaLock} iconColor="yellow.600" bg="yellow.50" borderColor="yellow.300"
                            title="Retido ML"
                            value={formatBRL(dados.kpis.retido_ml)}
                            footer="aguardando resolução"
                            valueColor="yellow.800"
                        />
                        <KpiCard
                            icon={FaHandHoldingUsd} iconColor="green.500" bg="green.50" borderColor="green.200"
                            title="Recuperado"
                            value={formatBRL(dados.kpis.recuperado)}
                            footer="valor que ficou"
                            valueColor="green.700"
                        />
                        <KpiCard
                            icon={FaCheckCircle} iconColor="purple.500"
                            title="Taxa Revisão OK"
                            value={dados.kpis.taxa_ok_pct != null ? `${dados.kpis.taxa_ok_pct}%` : '—'}
                            footer={`${dados.kpis.review_ok} ok · ${dados.kpis.review_fail} falha`}
                            valueColor="purple.700"
                        />
                        <KpiCard
                            icon={FaClock} iconColor="blue.500"
                            title="Tempo Médio"
                            value={dados.kpis.tempo_medio_resolucao_dias != null ? `${dados.kpis.tempo_medio_resolucao_dias} dias` : '—'}
                            footer="criação → encerramento"
                            valueColor="blue.700"
                        />
                    </SimpleGrid>

                    <ChartCard
                        title="Vendas × Devoluções por mês"
                        icon={FaChartLine}
                        height="380px"
                        headerRight={
                            <ButtonGroup size="xs" isAttached variant="outline">
                                <Button colorScheme="orange" variant={modoVendas === 'qtd' ? 'solid' : 'outline'} onClick={() => setModoVendas('qtd')}>
                                    Quantidade
                                </Button>
                                <Button colorScheme="orange" variant={modoVendas === 'valor' ? 'solid' : 'outline'} onClick={() => setModoVendas('valor')}>
                                    Valor (R$)
                                </Button>
                            </ButtonGroup>
                        }
                        footer={
                            <Text fontSize="11px" color="gray.500" mt={2}>
                                Devolução atribuída ao mês da <b>venda</b> (taxa real por safra). Linha tracejada e barra clara nos últimos ~2 meses indicam vendas ainda <b>em maturação</b> — podem gerar devoluções nas próximas semanas, então a taxa tende a subir.
                            </Text>
                        }
                    >
                        {vendasVsDevChart
                            ? <Bar data={vendasVsDevChart} options={optionsVendasVsDev} />
                            : <EmptyState text="Sem dados de vendas no período" />}
                    </ChartCard>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                        <ChartCard title="Volume mensal" icon={FaCalendarAlt}>
                            <Line data={charts.volumeData} options={optionsLine} />
                        </ChartCard>
                        <ChartCard title="Distribuição por destino do dinheiro" icon={FaArrowDown}>
                            <Doughnut data={charts.donutData} options={optionsDonut} />
                        </ChartCard>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                        <ChartCard title="Funil de status" icon={FaBoxes}>
                            <Bar data={charts.statusData} options={optionsVertical} />
                        </ChartCard>
                        <ChartCard title="Urgência atual" icon={FaExclamationTriangle}>
                            <Bar data={charts.urgenciaData} options={optionsVertical} />
                        </ChartCard>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                        <ChartCard title="Top 10 fornecedores (por qtd de devoluções)" icon={FaBoxes} height="380px">
                            {charts.fornecedoresData.labels.length
                                ? <Bar data={charts.fornecedoresData} options={optionsHorizontal} />
                                : <EmptyState text="Sem dados de fornecedor" />}
                        </ChartCard>
                        <ChartCard title="Top 10 produtos devolvidos" icon={FaBoxes} height="380px">
                            {charts.produtosData.labels.length
                                ? <Bar data={charts.produtosData} options={optionsHorizontal} />
                                : <EmptyState text="Sem dados de produto" />}
                        </ChartCard>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                        <ChartCard title="Top motivos reais (reportado pelo comprador)" icon={FaCommentDots} height="380px">
                            {charts.motivosReaisData.labels.length
                                ? <Bar data={charts.motivosReaisData} options={optionsHorizontal} />
                                : <EmptyState text="Nenhum motivo (campo problem) disponível no período" />}
                        </ChartCard>
                        <ChartCard title="Top resoluções / estágios (título ML)" icon={FaFlag} height="380px">
                            {charts.resolucoesData.labels.length
                                ? <Bar data={charts.resolucoesData} options={optionsHorizontal} />
                                : <EmptyState text="Sem resoluções registradas" />}
                        </ChartCard>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1 }} spacing={4}>
                        <ChartCard title="Por conta" icon={FaBoxes} height="380px">
                            <Bar data={charts.porContaData} options={optionsDuplo} />
                        </ChartCard>
                    </SimpleGrid>
                </VStack>
            )}
        </Box>
    );
};

const truncate = (s, n) => (s && s.length > n ? s.substring(0, n - 1) + '…' : s || '');

const EmptyState = ({ text }) => (
    <Flex h="100%" align="center" justify="center">
        <Text color="gray.400" fontSize="sm">{text}</Text>
    </Flex>
);

export default DevolucoesMetricas;
